import { useEffect, useMemo, useState } from 'react';
import { useCrmData } from '../../contexts/CrmDataContext';
import {
  fetchDashboardMonthlySales,
  formatSalesAmountFull,
  formatSalesAmountShort,
  monthShortLabel,
  type DashboardMonthlySales,
} from '../../lib/dashboardMonthlySales';

export function DashboardMonthlySalesChart() {
  const {
    username,
    actorSalespersonCodes,
    salespersonCodesByUsername,
    userFilter,
    canUseUserFilter,
  } = useCrmData();

  const [data, setData] = useState<DashboardMonthlySales | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** null = company-wide (omit codes); [] = empty; string[] = filter */
  const requestCodes = useMemo((): string[] | null => {
    if (!canUseUserFilter) {
      return actorSalespersonCodes;
    }
    if (userFilter === 'All') {
      return null;
    }
    if (userFilter === 'Me') {
      return actorSalespersonCodes;
    }
    const key = String(userFilter).trim().toLowerCase();
    const fromMap = Object.entries(salespersonCodesByUsername).find(
      ([u]) => u.trim().toLowerCase() === key,
    );
    if (fromMap) return fromMap[1] ?? [];
    if (key === username.trim().toLowerCase()) return actorSalespersonCodes;
    return [];
  }, [
    canUseUserFilter,
    userFilter,
    actorSalespersonCodes,
    salespersonCodesByUsername,
    username,
  ]);

  const requestCodesKey =
    requestCodes === null
      ? `__all__:${canUseUserFilter ? '1' : '0'}`
      : requestCodes.map((c) => c.trim().toUpperCase()).sort().join(',');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const requestAll = canUseUserFilter && userFilter === 'All';
    void fetchDashboardMonthlySales(requestCodes, { requestAll })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((e) => {
        if (!cancelled) {
          setData(null);
          setError(e instanceof Error ? e.message : 'Failed to load sales.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- requestCodesKey stabilizes array identity
  }, [requestCodesKey, canUseUserFilter, userFilter]);

  const maxThis = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, ...data.months.map((m) => Math.abs(m.this_year)));
  }, [data]);

  const maxLast = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, ...data.months.map((m) => Math.abs(m.last_year)));
  }, [data]);

  const totals = useMemo(() => {
    if (!data) return { thisYear: 0, lastYear: 0 };
    return {
      thisYear: data.months.reduce((s, m) => s + m.this_year, 0),
      lastYear: data.months.reduce((s, m) => s + m.last_year, 0),
    };
  }, [data]);

  return (
    <div className="card dashboard-panel dashboard-panel--sales">
      <div className="dashboard-sales__head">
        <div>
          <h2 className="detail-section-title">Annual sales by month</h2>
        </div>
        {data && !loading ? (
          <div className="dashboard-sales__totals" aria-label="Year totals">
            <span className="dashboard-sales__total dashboard-sales__total--this">
              <span className="dashboard-sales__swatch" aria-hidden />
              {data.year}: {formatSalesAmountFull(totals.thisYear)}
            </span>
            <span className="dashboard-sales__total dashboard-sales__total--last">
              <span className="dashboard-sales__swatch" aria-hidden />
              {data.last_year}: {formatSalesAmountFull(totals.lastYear)}
            </span>
          </div>
        ) : null}
      </div>

      {loading ? <p className="dashboard-sales__status">Loading sales…</p> : null}
      {error ? <p className="dashboard-sales__status dashboard-sales__status--error">{error}</p> : null}
      {!loading && !error && data?.meta?.salesperson_scope === 'none' && canUseUserFilter ? (
        <p className="dashboard-sales__status dashboard-sales__status--error">
          Proxy returned no sales scope (elevated=
          {data.meta.elevated === true ? 'true' : 'false'}, rev=
          {data.meta.proxy_rev ?? 'missing'}). Re-upload public/account-sales-proxy.php with Overwrite.
        </p>
      ) : null}

      {!loading && !error && data ? (
        <div className="dashboard-sales__chart" role="list" aria-label="Monthly sales comparison">
          {data.months.map((month) => {
            // Independent scales so a tiny prior year stays readable when YoY differs a lot.
            const thisPct = Math.max(
              month.this_year === 0 ? 0 : 3,
              Math.round((Math.abs(month.this_year) / maxThis) * 100),
            );
            const lastPct = Math.max(
              month.last_year === 0 ? 0 : 3,
              Math.round((Math.abs(month.last_year) / maxLast) * 100),
            );
            return (
              <div key={month.month} className="dashboard-sales__col" role="listitem">
                <div className="dashboard-sales__values">
                  <span
                    className="dashboard-sales__value dashboard-sales__value--this"
                    title={`${data.year}: ${formatSalesAmountFull(month.this_year)}`}
                  >
                    {formatSalesAmountShort(month.this_year)}
                  </span>
                  <span
                    className="dashboard-sales__value dashboard-sales__value--last"
                    title={`${data.last_year}: ${formatSalesAmountFull(month.last_year)}`}
                  >
                    {formatSalesAmountShort(month.last_year)}
                  </span>
                </div>
                <div className="dashboard-sales__bars" aria-hidden>
                  <span
                    className={`dashboard-sales__bar dashboard-sales__bar--this${
                      month.this_year < 0 ? ' is-negative' : ''
                    }`}
                    style={{ height: `${thisPct}%` }}
                  />
                  <span
                    className={`dashboard-sales__bar dashboard-sales__bar--last${
                      month.last_year < 0 ? ' is-negative' : ''
                    }`}
                    style={{ height: `${lastPct}%` }}
                  />
                </div>
                <span className="dashboard-sales__label">{monthShortLabel(month.month)}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
